import React from "react";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clock,
  Cloud,
  CloudOff,
  HelpCircle,
  Loader2,
  Lock,
  Shield
} from "lucide-react";

export const intakeTokens = {
  page: "min-h-screen bg-[#f4f6fb] text-slate-800 font-sans antialiased",
  shell: "max-w-[1400px] w-full mx-auto",
  card: "rounded-xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(8,21,73,0.05),0_8px_24px_-6px_rgba(8,21,73,0.08)]",
  cardSoft: "rounded-xl border border-slate-200/80 bg-white/95",
  input:
    "w-full h-10 px-3.5 bg-slate-50/80 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-ctb-blue focus:ring-2 focus:ring-ctb-blue/10 transition",
  textarea:
    "w-full min-h-[88px] p-3.5 bg-slate-50/80 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-ctb-blue focus:ring-2 focus:ring-ctb-blue/10 transition resize-y",
  choice:
    "px-3.5 py-2.5 rounded-lg border text-left text-sm font-medium transition flex items-center justify-between gap-2",
  choiceOn: "bg-ctb-blue-0/50 border-ctb-blue/35 text-ctb-blue shadow-sm",
  choiceOff: "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50",
  btnPrimary:
    "inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-lg bg-ctb-blue text-white text-sm font-semibold shadow-sm shadow-ctb-blue/15 hover:bg-ctb-blue/90 transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none",
  btnSecondary:
    "inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 bg-white text-ctb-blue text-sm font-semibold hover:bg-slate-50 transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none",
  btnSubmit:
    "inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700 transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
} as const;

export const IntakePageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={`${intakeTokens.page} py-8 px-4 relative overflow-hidden flex items-center justify-center min-h-screen`}>
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-ctb-blue/5 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-emerald-500/5 blur-3xl" />
    </div>
    <div className="relative z-10 flex flex-col items-center w-full max-w-7xl mx-auto justify-center">{children}</div>
  </div>
);

export const IntakeProgressHeader: React.FC<{
  current: number;
  total: number;
  clientName?: string;
}> = ({ current, total, clientName }) => {
  const pct = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  const remaining = Math.max(1, Math.ceil((total - current) * 1.5));

  return (
    <div className={`${intakeTokens.card} p-4 mb-4 lg:hidden`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-semibold text-ctb-blue">
          Step {current + 1} of {total}
        </span>
        <span className="text-[10px] text-slate-500 flex items-center gap-1">
          <Clock size={11} />
          ~{remaining} min left
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-ctb-blue to-ctb-blue-600 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {clientName && (
        <p className="text-[10px] text-slate-500 mt-2 truncate">{clientName}</p>
      )}
    </div>
  );
};

export const IntakeStepRail: React.FC<{
  sections: { id: string; title: string; questions?: unknown[] }[];
  current: number;
  onSelect: (idx: number) => void;
  canNavigate: boolean;
  completedSections?: Set<string>;
}> = ({ sections, current, onSelect, canNavigate, completedSections = new Set() }) => (
  <aside className={`${intakeTokens.card} p-5 lg:sticky lg:top-6 space-y-5 hidden lg:block`}>
    <div className="space-y-1 pb-4 border-b border-slate-100">
      <div className="flex items-center gap-2 text-ctb-blue">
        <Shield size={16} strokeWidth={2} />
        <span className="text-[11px] font-semibold uppercase tracking-wide">Security scoping</span>
      </div>
      <h1 className="text-base font-bold text-slate-900 leading-snug">Build your scope</h1>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Short sections, autosaved as you go. You can return anytime using this link.
      </p>
    </div>

    <nav className="relative pl-1 space-y-1" aria-label="Form sections">
      <div className="absolute left-[24px] top-2 bottom-2 w-px bg-slate-200" />
      {sections.map((sec, idx) => {
        const done = completedSections.has(sec.id) || idx < current;
        const active = idx === current;
        return (
          <button
            key={sec.id}
            type="button"
            disabled={!canNavigate && idx > current}
            onClick={() => onSelect(idx)}
            className={`relative w-full flex items-center gap-3 text-left py-2 px-2 rounded-lg transition ${
              active ? "bg-ctb-blue-0/40" : "hover:bg-slate-50"
            }`}
          >
            <span
              className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold border-2 transition ${
                done
                  ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                  : active
                    ? "bg-ctb-blue border-ctb-blue text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-400"
              }`}
            >
              {done ? <Check size={12} strokeWidth={3} /> : idx + 1}
            </span>
            <span className="min-w-0 pt-0.5">
              <span
                className={`block text-xs font-semibold truncate ${
                  active ? "text-ctb-blue" : done ? "text-slate-700" : "text-slate-500"
                }`}
              >
                {sec.title}
              </span>
              <span className="text-[10px] text-slate-400">
                {(sec.questions?.length as number) || 0} questions
              </span>
            </span>
          </button>
        );
      })}
    </nav>

    <p className="text-[10px] text-slate-500 leading-relaxed p-3 rounded-lg bg-slate-50 border border-slate-100">
      Questions you&apos;re unsure about can be marked &ldquo;Not sure&rdquo; — we&apos;ll use sensible defaults.
    </p>
  </aside>
);

export const IntakeSaveStatus: React.FC<{
  isReadOnly: boolean;
  status: "idle" | "saving" | "saved" | "error";
}> = ({ isReadOnly, status }) => {
  if (isReadOnly) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">
        <Lock size={12} />
        Submitted
      </span>
    );
  }
  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-ctb-blue">
        <Loader2 size={12} className="animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 px-2.5 py-1 rounded-lg">
        <CloudOff size={12} />
        Save failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#58CC02]">
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      Autosave on
    </span>
  );
};

export const IntakeQuestionCard: React.FC<{
  children: React.ReactNode;
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  className?: string;
}> = ({ children, label, required, hint, error, className = "" }) => (
  <div
    className={`space-y-3 transition-colors ${className} ${
      error ? "p-3.5 bg-rose-50/40 border border-rose-250/50 rounded-xl" : ""
    }`}
  >
    <div className="flex items-start justify-between gap-3 relative">
      <div className="text-sm font-semibold text-slate-700 leading-snug pr-2">
        {label}
        {required && <span className="text-[#58CC02] ml-0.5">*</span>}
      </div>
      <div className="relative z-10">{hint}</div>
    </div>
    {children}
    {error && (
      <p className="text-xs text-rose-700 flex items-start gap-1.5 bg-rose-50 border border-rose-100 rounded-lg px-2.5 py-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        {error}
      </p>
    )}
  </div>
);

export const IntakeTbcToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}> = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange();
    }}
    onMouseDown={(e) => {
      e.stopPropagation();
    }}
    className="inline-flex items-center gap-2 shrink-0 text-[11px] font-medium text-slate-500 hover:text-ctb-blue transition cursor-pointer relative z-50 pointer-events-auto p-1 -m-1"
    style={{ pointerEvents: 'auto' }}
  >
    <HelpCircle size={13} />
    Not sure yet
    <span
      className={`relative w-8 h-4 rounded-full transition-colors ${
        checked ? "bg-ctb-blue" : "bg-slate-200"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </span>
  </button>
);

export const IntakeSuccessPanel: React.FC<{
  clientName?: string;
  clientEmail?: string;
}> = ({ clientName, clientEmail }) => (
  <div className={`${intakeTokens.card} p-8 max-w-lg mx-auto text-center space-y-5`}>
    <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center text-emerald-600">
      <CircleCheck size={36} strokeWidth={1.5} />
    </div>
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-slate-900">Thanks - we’ve received your scoping information.</h2>
      <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
        Our team will review the details and either send through the next steps/proposal or get in touch if we need any further information.
      </p>
      <p className="text-xs text-slate-500 font-medium">
        You’ll also receive a confirmation by email.
      </p>
    </div>
    {(clientName || clientEmail) && (
      <div className="text-left text-sm space-y-2 p-4 rounded-lg bg-slate-50 border border-slate-100">
        {clientName && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Company</span>
            <span className="font-semibold text-slate-800">{clientName}</span>
          </div>
        )}
        {clientEmail && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Email</span>
            <span className="font-semibold text-slate-800">{clientEmail}</span>
          </div>
        )}
      </div>
    )}
    <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200/80 rounded-lg px-3 py-2.5 flex items-start gap-2 text-left">
      <Lock size={14} className="shrink-0 mt-0.5" />
      This form is now read-only. Contact your specialist if you need changes.
    </p>
    <div className="pt-2">
      <a
        href="/dashboard"
        className="inline-flex w-full items-center justify-center h-10 px-5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:text-slate-900 hover:bg-slate-50 text-sm font-semibold shadow-sm transition active:scale-[0.98]"
      >
        Back to Dashboard
      </a>
    </div>
  </div>
);

export const IntakeLoadingState: React.FC<{ message?: string }> = ({
  message = "Loading your secure form…"
}) => (
  <div className={`${intakeTokens.page} flex flex-col items-center justify-center gap-3`}>
    <Loader2 className="w-9 h-9 text-ctb-blue animate-spin" />
    <p className="text-sm text-slate-500 font-medium">{message}</p>
  </div>
);
